import {
  isProviderDriverKind,
  isProviderAvailable,
  type ModelSelection,
  type ProviderDriverKind,
  resetScopedSettingOverrides,
  type ServerProvider,
  ServerSettings,
  type ServerSettingsPatch,
  upsertScopedSettingOverrides,
} from "@cleo/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { deepMerge } from "./Struct.ts";
import { fromLenientJson } from "./schemaJson.ts";
import { createModelSelection } from "./model.ts";
import {
  getBackgroundActivityBaseProfile,
  normalizeBackgroundActivitySettings,
  normalizeServerBackgroundActivitySettings,
  resolveBackgroundActivitySettings,
} from "./backgroundActivitySettings.ts";

const ServerSettingsJson = fromLenientJson(ServerSettings);
const decodeServerSettingsJson = Schema.decodeUnknownOption(ServerSettingsJson);

export interface PersistedServerObservabilitySettings {
  readonly otlpTracesUrl: string | undefined;
  readonly otlpMetricsUrl: string | undefined;
}

export function normalizePersistedServerSettingString(
  value: string | null | undefined,
): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

export function extractPersistedServerObservabilitySettings(input: {
  readonly observability?: {
    readonly otlpTracesUrl?: string;
    readonly otlpMetricsUrl?: string;
  };
}): PersistedServerObservabilitySettings {
  return {
    otlpTracesUrl: normalizePersistedServerSettingString(input.observability?.otlpTracesUrl),
    otlpMetricsUrl: normalizePersistedServerSettingString(input.observability?.otlpMetricsUrl),
  };
}

export function parsePersistedServerObservabilitySettings(
  raw: string,
): PersistedServerObservabilitySettings {
  const decoded = decodeServerSettingsJson(raw);
  if (Option.isSome(decoded)) {
    return extractPersistedServerObservabilitySettings(decoded.value);
  }
  return { otlpTracesUrl: undefined, otlpMetricsUrl: undefined };
}

export function applyServerSettingsPatch(
  current: ServerSettings,
  patch: ServerSettingsPatch,
): ServerSettings {
  const {
    automaticGitFetchInterval,
    providerHealthRefreshInterval,
    backgroundActivityProfile,
    backgroundActivity,
    worldWorkspaceBindingRemovals,
    providerInstanceRemovals,
    scopedSettingOverrides,
    scopedSettingOverrideResets,
    ...patchForMerge
  } = patch;
  const currentBackgroundActivity = normalizeServerBackgroundActivitySettings(current);
  const backgroundActivityPatch =
    backgroundActivityProfile !== undefined
      ? {
          schemaVersion: 1 as const,
          profile:
            automaticGitFetchInterval !== undefined || providerHealthRefreshInterval !== undefined
              ? ("custom" as const)
              : backgroundActivityProfile,
          ...(automaticGitFetchInterval !== undefined || providerHealthRefreshInterval !== undefined
            ? { baseProfile: backgroundActivityProfile }
            : {}),
          overrides: {
            ...(automaticGitFetchInterval !== undefined ? { automaticGitFetchInterval } : {}),
            ...(providerHealthRefreshInterval !== undefined
              ? { providerHealthRefreshInterval }
              : {}),
          },
        }
      : automaticGitFetchInterval !== undefined || providerHealthRefreshInterval !== undefined
        ? {
            schemaVersion: 1 as const,
            profile: "custom" as const,
            baseProfile: getBackgroundActivityBaseProfile(currentBackgroundActivity),
            overrides: {
              ...(currentBackgroundActivity.profile === "custom"
                ? currentBackgroundActivity.overrides
                : {}),
              ...(automaticGitFetchInterval !== undefined ? { automaticGitFetchInterval } : {}),
              ...(providerHealthRefreshInterval !== undefined
                ? { providerHealthRefreshInterval }
                : {}),
            },
          }
        : undefined;
  const merged = deepMerge(current, patchForMerge);
  // Binding removal is an explicit operation, and it wins over a same-patch
  // bind: the listed worldKeys' rows are deleted from the merged map.
  const bindingRemovals = new Set(worldWorkspaceBindingRemovals ?? []);
  const afterRemovals =
    bindingRemovals.size === 0
      ? merged
      : {
          ...merged,
          worldWorkspaceBindings: Object.fromEntries(
            Object.entries(merged.worldWorkspaceBindings).filter(
              ([worldKey]) => !bindingRemovals.has(worldKey),
            ),
          ),
        };
  // A patched `folders` array replaces wholesale: the generic deep merge
  // index-merges arrays (a shrink would corrupt into an object), and the
  // list's order is the person's exact statement.
  const bindingsPatch = patchForMerge.worldWorkspaceBindings;
  const afterBindings =
    bindingsPatch === undefined
      ? afterRemovals
      : {
          ...afterRemovals,
          worldWorkspaceBindings: Object.fromEntries(
            Object.entries(afterRemovals.worldWorkspaceBindings).map(([worldKey, row]) => {
              const patched = bindingsPatch[worldKey];
              return [
                worldKey,
                {
                  ...row,
                  ...(patched?.folders === undefined ? {} : { folders: patched.folders }),
                  ...(patched?.sourceFolders === undefined
                    ? {}
                    : { sourceFolders: patched.sourceFolders }),
                },
              ];
            }),
          ),
        };
  // UP7: the override record is one row per (scope, key). A patched row
  // replaces the row with its identity; a reset removes exactly the named
  // rows after the upserts, so a row both set and reset ends up reset and the
  // layer above it returns. Never index-merged.
  const afterOverrides =
    scopedSettingOverrides === undefined && scopedSettingOverrideResets === undefined
      ? afterBindings
      : {
          ...afterBindings,
          scopedSettingOverrides: resetScopedSettingOverrides(
            upsertScopedSettingOverrides(
              afterBindings.scopedSettingOverrides,
              scopedSettingOverrides ?? [],
            ),
            scopedSettingOverrideResets ?? [],
          ),
        };
  const nextWithReplacementsBase = {
    ...afterOverrides,
    // A model and its options are one complete choice. Deep merging would
    // retain the previous model's reasoning when the new choice omits it.
    ...(patch.defaultSessionModel !== undefined
      ? { defaultSessionModel: patch.defaultSessionModel }
      : {}),
    ...(patch.textGenerationModelSelection !== undefined
      ? { textGenerationModelSelection: patch.textGenerationModelSelection }
      : {}),
    ...(backgroundActivity !== undefined
      ? {
          backgroundActivity: {
            ...deepMerge(currentBackgroundActivity, backgroundActivity),
            ...(backgroundActivity.overrides !== undefined
              ? { overrides: backgroundActivity.overrides }
              : {}),
          },
        }
      : { backgroundActivity: currentBackgroundActivity }),
    ...(backgroundActivity === undefined && backgroundActivityPatch !== undefined
      ? { backgroundActivity: backgroundActivityPatch }
      : {}),
    // Each named account entry replaces its row whole (omitted fields clear);
    // an entry the patch does not name stays; removal is the named operation.
    ...(patch.providerInstances !== undefined || providerInstanceRemovals !== undefined
      ? {
          providerInstances: Object.fromEntries(
            Object.entries({ ...current.providerInstances, ...patch.providerInstances }).filter(
              ([instanceId]) => !(providerInstanceRemovals ?? []).some((id) => id === instanceId),
            ),
          ) as ServerSettings["providerInstances"],
        }
      : {}),
    ...(automaticGitFetchInterval !== undefined ? { automaticGitFetchInterval } : {}),
    ...(providerHealthRefreshInterval !== undefined ? { providerHealthRefreshInterval } : {}),
  };
  const normalizedBackgroundActivity = normalizeBackgroundActivitySettings(
    nextWithReplacementsBase.backgroundActivity,
  );
  const resolvedBackgroundActivity = resolveBackgroundActivitySettings(
    normalizedBackgroundActivity,
  );
  const nextWithReplacements = {
    ...nextWithReplacementsBase,
    backgroundActivity: normalizedBackgroundActivity,
    automaticGitFetchInterval: resolvedBackgroundActivity.automaticGitFetchInterval,
    providerHealthRefreshInterval: resolvedBackgroundActivity.providerHealthRefreshInterval,
    backgroundActivityProfile: resolvedBackgroundActivity.profile,
  };
  return nextWithReplacements;
}
